import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft, 
  User, 
  Mail, 
  Briefcase, 
  Calendar, 
  Award, 
  Send, 
  Eye, 
  Trash2, 
  Download,
  FileCheck
} from "lucide-react";

export default function ExperienceLetter() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    employeeId: "",
    employeeName: "",
    employeeEmail: "",
    designation: "",
    department: "",
    dateOfJoining: "",
    lastWorkingDate: "",
    experienceYears: "",
    conduct: "",
    issuedBy: "",
    remarks: "",
  });

  const [showPreview, setShowPreview] = useState(false);
  const [sentLetters, setSentLetters] = useState([]);

  // Font Styles
  const poppinsStyle = { fontFamily: "'Poppins', sans-serif" };
  const robotoStyle = { fontFamily: "'Roboto', sans-serif" };

  const calculateExperience = (doj, lwd) => {
    if (!doj || !lwd) return "";
    const start = new Date(doj);
    const end = new Date(lwd);
    if (end < start) return "";

    let years = end.getFullYear() - start.getFullYear();
    let months = end.getMonth() - start.getMonth();
    let days = end.getDate() - start.getDate();

    if (days < 0) {
      months -= 1;
      days += new Date(end.getFullYear(), end.getMonth(), 0).getDate();
    }
    if (months < 0) {
      years -= 1;
      months += 12;
    }

    return `${years} Years ${months} Months`;
  };

  const handleChange = (key, value) => {
    setForm((prev) => {
      const updated = { ...prev, [key]: value };
      if (key === "dateOfJoining" || key === "lastWorkingDate") {
        updated.experienceYears = calculateExperience(
          updated.dateOfJoining,
          updated.lastWorkingDate
        );
      }
      return updated;
    });
  };

  const handleSend = () => {
    setSentLetters((prev) => [
      { ...form, sentAt: new Date().toLocaleString() },
      ...prev,
    ]);
    setShowPreview(false);
    alert(`Experience letter generated for ${form.employeeName}`);
  };

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 md:p-10" style={robotoStyle}>
      <div className="max-w-6xl mx-auto">
        
        {/* Header */}
        <div className="mb-8">
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center gap-2 text-slate-500 hover:text-teal-600 mb-4 transition-colors font-medium text-sm"
          >
            <ArrowLeft size={18} /> Back to Dashboard
          </button>
          <h1 className="text-3xl font-bold text-slate-800" style={poppinsStyle}>Experience Letter</h1>
          <p className="text-slate-500">Verify and issue service experience certificates.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Form Section */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 md:p-8">
              <div className="flex items-center gap-2 mb-6 text-teal-600">
                <User size={20} />
                <h2 className="font-semibold text-slate-800" style={poppinsStyle}>Employee Information</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Input label="Employee ID" value={form.employeeId} onChange={(v) => handleChange("employeeId", v)} />
                <Input label="Full Name" value={form.employeeName} onChange={(v) => handleChange("employeeName", v)} />
                <Input label="Email Address" type="email" value={form.employeeEmail} onChange={(v) => handleChange("employeeEmail", v)} />
                <Input label="Designation" value={form.designation} onChange={(v) => handleChange("designation", v)} />
                <Input label="Department" value={form.department} onChange={(v) => handleChange("department", v)} />
                <Input type="date" label="Date of Joining" value={form.dateOfJoining} onChange={(v) => handleChange("dateOfJoining", v)} />
                <Input type="date" label="Last Working Date" value={form.lastWorkingDate} onChange={(v) => handleChange("lastWorkingDate", v)} />
                <Input label="Total Experience" value={form.experienceYears} readOnly className="bg-slate-50" />
                <Select 
                  label="General Conduct" 
                  value={form.conduct} 
                  onChange={(v) => handleChange("conduct", v)} 
                  options={["Excellent", "Very Good", "Good", "Satisfactory"]} 
                />
                <Input label="Issued By (HR/Admin)" value={form.issuedBy} onChange={(v) => handleChange("issuedBy", v)} />
              </div>

              <div className="mt-6">
                <label className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Remarks / Special Mentions</label>
                <textarea
                  className="w-full border border-slate-200 rounded-xl px-4 py-3 h-28 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all text-sm bg-slate-50/30"
                  value={form.remarks}
                  placeholder="Note specific contributions or project achievements..."
                  onChange={(e) => handleChange("remarks", e.target.value)}
                />
              </div>

              <div className="mt-8">
                <button
                  onClick={() => setShowPreview(true)}
                  className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-3 rounded-xl font-semibold flex items-center gap-2 transition-all shadow-lg shadow-teal-600/20"
                >
                  <Eye size={18} /> Preview Certificate
                </button>
              </div>
            </div>

            {/* History Table */}
            {sentLetters.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="p-6 border-b border-slate-100 bg-slate-50/50">
                  <h3 className="font-semibold text-slate-800" style={poppinsStyle}>Recent Generations</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead className="bg-slate-50 text-slate-400 text-[10px] uppercase font-bold tracking-widest">
                      <tr>
                        <th className="px-6 py-4">Employee</th>
                        <th className="px-6 py-4">Designation</th>
                        <th className="px-6 py-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {sentLetters.map((l, i) => (
                        <tr key={i} className="hover:bg-slate-50/50 transition-colors group">
                          <td className="px-6 py-4">
                            <p className="font-medium text-slate-900 text-sm">{l.employeeName}</p>
                            <p className="text-xs text-slate-500">{l.employeeEmail}</p>
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-600">{l.designation}</td>
                          <td className="px-6 py-4">
                            <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button onClick={() => { setForm(l); setShowPreview(true); }} className="p-2 text-slate-400 hover:text-teal-600 transition-colors"><Edit2 size={16} /></button>
                              <button className="p-2 text-slate-400 hover:text-red-600 transition-colors" onClick={() => setSentLetters(prev => prev.filter((_, idx) => idx !== i))}><Trash2 size={16} /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Preview Panel */}
          <div className="lg:col-span-1">
            {showPreview ? (
              <div className="bg-white rounded-2xl border-2 border-teal-500 shadow-2xl p-6 md:p-8 sticky top-10 animate-in fade-in slide-in-from-right-4 duration-300">
                <div className="flex items-center gap-2 mb-6 pb-4 border-b">
                  <FileCheck className="text-teal-600" />
                  <span className="font-bold text-xs uppercase tracking-widest text-slate-400" style={poppinsStyle}>Letter Preview</span>
                </div>
                
                <div className="space-y-6 text-slate-700 text-sm leading-relaxed">
                  <p className="font-bold text-center text-lg border-b pb-4 mb-8">TO WHOMSOEVER IT MAY CONCERN</p>
                  
                  <p>This is to certify that <span className="font-bold text-slate-900">{form.employeeName || "[Name]"}</span> (Employee ID: <span className="font-bold">{form.employeeId || "[ID]"}</span>) was an integral part of <span className="font-semibold">Our Organization</span>.</p>
                  
                  <p>They served as <span className="font-bold text-teal-600">{form.designation || "[Designation]"}</span> in the <span className="font-semibold">{form.department || "[Department]"}</span> department from <span className="font-semibold">{form.dateOfJoining || "[DOJ]"}</span> to <span className="font-semibold">{form.lastWorkingDate || "[LWD]"}</span>.</p>
                  
                  <div className="bg-slate-50 p-4 rounded-xl space-y-2 text-xs">
                    <p><strong>Total Tenure:</strong> {form.experienceYears || "Not Calculated"}</p>
                    <p><strong>Performance Conduct:</strong> {form.conduct || "Pending"}</p>
                  </div>

                  <p>{form.remarks || "During their tenure, we found them to be hardworking and committed to their responsibilities."}</p>

                  <div className="pt-10">
                    <p className="font-bold">{form.issuedBy || "[Authorized Signatory]"}</p>
                    <p className="text-xs text-slate-400 font-medium">Human Resources Department</p>
                  </div>
                </div>

                <div className="mt-10 flex flex-col gap-3">
                  <button 
                    onClick={handleSend}
                    className="w-full bg-slate-900 text-white py-3 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-teal-600 transition-all flex items-center justify-center gap-2"
                  >
                    <Send size={16} /> Finalize & Send
                  </button>
                  <button 
                    onClick={() => setShowPreview(false)}
                    className="w-full text-slate-400 hover:text-slate-600 font-bold text-[10px] uppercase tracking-widest py-2"
                  >
                    Edit Information
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-slate-100 rounded-2xl border-2 border-dashed border-slate-200 p-12 text-center sticky top-10">
                <Award className="mx-auto text-slate-300 mb-4" size={48} />
                <p className="text-slate-400 text-sm font-medium">Fill out the form and click preview<br/>to generate the letter draft.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Sub-components
function Input({ label, value, onChange, type = "text", readOnly = false, className = "" }) {
  return (
    <div className="space-y-1.5">
      <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</label>
      <input
        type={type}
        value={value}
        readOnly={readOnly}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all bg-slate-50/30 ${className}`}
      />
    </div>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <div className="space-y-1.5">
      <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all bg-slate-50/30 appearance-none cursor-pointer"
      >
        <option value="">Select Option</option>
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </div>
  );
}

// Icon wrapper for list
function Edit2(props) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={props.size} height={props.size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>
  );
}